<template>
    <CardLoader v-if="loading" class="my-3" />
    <CardLoader v-if="loading" class="my-3" />

    <div class="w-100" v-else>
        <div class="min-vh-100">
            <div class="px-4">
                <!-- Filter Bar -->
                <div class="card mb-4 mt-3">
                    <div class="card-body">
                        <div class="d-flex align-items-center justify-content-between mb-3">
                            <h6 class="m-0"><i class="material-icons align-middle me-1">filter_alt</i>Filters</h6>
                            <div class="d-flex gap-2">
                                <button class="btn btn-sm btn-success m-0" @click="applyFilters">
                                    <i class="material-icons text-sm me-1">search</i>Apply
                                </button>
                                <button class="btn btn-sm btn-outline-secondary m-0" @click="clearFilters">
                                    <i class="material-icons text-sm me-1">clear</i>Clear
                                </button>
                            </div>
                        </div>
                        <div class="row g-3">
                            <!-- Status -->
                            <div class="col-md-2">
                                <label class="form-label small mb-1">Status</label>
                                <select class="form-select form-select-sm" v-model="filters.status">
                                    <option value="active">Active</option>
                                    <option value="inactive">Inactive</option>
                                    <option value="suspended">Suspended</option>
                                    <option value="all">All</option>
                                </select>
                            </div>
                            <!-- Signup Date Range -->
                            <div class="col-md-2">
                                <label class="form-label small mb-1">Signup From</label>
                                <input type="date" class="form-control form-control-sm" v-model="filters.signup_date_from" />
                            </div>
                            <div class="col-md-2">
                                <label class="form-label small mb-1">Signup To</label>
                                <input type="date" class="form-control form-control-sm" v-model="filters.signup_date_to" />
                            </div>
                            <!-- Balance Range -->
                            <div class="col-md-1">
                                <label class="form-label small mb-1">Bal Min</label>
                                <input type="number" step="0.01" class="form-control form-control-sm" v-model="filters.balance_min" placeholder="0" />
                            </div>
                            <div class="col-md-1">
                                <label class="form-label small mb-1">Bal Max</label>
                                <input type="number" step="0.01" class="form-control form-control-sm" v-model="filters.balance_max" placeholder="∞" />
                            </div>
                            <!-- Org/Location ID search -->
                            <div class="col-md-2">
                                <label class="form-label small mb-1">Org ID</label>
                                <input type="text" class="form-control form-control-sm" v-model="filters.organization_id"
                                    placeholder="Org ID" @keyup.enter="applyFilters" />
                            </div>
                            <div class="col-md-2">
                                <label class="form-label small mb-1">Location ID</label>
                                <input type="text" class="form-control form-control-sm" v-model="filters.location_id"
                                    placeholder="Loc ID" @keyup.enter="applyFilters" />
                            </div>
                        </div>
                        <!-- Text search -->
                        <div class="row g-3 mt-1">
                            <div class="col-md-2">
                                <label class="form-label small mb-1">Location Count (min)</label>
                                <input type="number" min="0" class="form-control form-control-sm"
                                    v-model.number="filters.location_count" placeholder="1" />
                            </div>
                            <div class="col-md-2">
                                <label class="form-label small mb-1">Location Status</label>
                                <select class="form-select form-select-sm" v-model="filters.location_status">
                                    <option value="active">Active</option>
                                    <option value="inactive">Inactive</option>
                                    <option value="suspended">Suspended</option>
                                    <option value="all">All</option>
                                </select>
                            </div>
                            <div class="col-md-2">
                                <label class="form-label small mb-1">Persons Count (min)</label>
                                <input type="number" min="0" class="form-control form-control-sm"
                                    v-model.number="filters.persons_count" placeholder="1" />
                            </div>
                            <div class="col-md-6">
                                <label class="form-label small mb-1">Search (name / email)</label>
                                <input type="text" class="form-control form-control-sm" v-model="filters.search"
                                    placeholder="Organization name or email..." @keyup.enter="applyFilters" />
                            </div>
                        </div>
                    </div>
                </div>

                <!-- No Data State -->
                <div v-if="!loading && organizations.length === 0"
                    class="d-flex flex-column align-items-center justify-content-center" style="min-height: 40vh;">
                    <i class="material-icons" style="font-size:64px;color:#ccc;">business</i>
                    <h5 class="text-secondary mt-3 mb-1">No Organizations Found</h5>
                    <p class="text-muted small">Try adjusting your filters.</p>
                </div>

                <!-- Organizations Content -->
                <div v-else-if="!loading && organizations.length > 0" class="organizations-list">
                    <div v-for="org in organizations" :key="org.organizationid" class="mb-4">
                        <!-- Organization Header Card -->
                        <div class="card">
                            <div class="card-header bg-success text-white py-2">
                                <div class="d-flex justify-content-between align-items-center">
                                    <div class="d-flex align-items-center gap-2">
                                        <span class="badge bg-white text-success fw-bold cursor-pointer"
                                            @click="copyToClipboard(org.organizationid, 'Org ID')"
                                            title="Click to copy Org ID">
                                            #{{ org.organizationid }}
                                        </span>
                                        <h6 class="text-white mb-0">{{ org.organizationname }}</h6>
                                    </div>
                                    <div class="d-flex align-items-center gap-2">
                                        <span class="badge bg-white text-dark" v-if="org.balance !== null && org.balance !== undefined">
                                            Balance: ${{ formatBalance(org.balance) }}
                                        </span>
                                        <span class="badge" :class="org.status === 'active' ? 'border border-white' : 'bg-danger'">
                                            {{ org.status }}
                                        </span>
                                        <button class="btn btn-sm btn-light text-success m-0 py-0 px-2"
                                            @click="toggleExpand(org.organizationid)"
                                            :title="expandedOrgs[org.organizationid] ? 'Collapse' : 'Expand'">
                                            <i class="material-icons text-sm">
                                                {{ expandedOrgs[org.organizationid] ? 'expand_less' : 'expand_more' }}
                                            </i>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Expandable Org Details -->
                        <div v-if="expandedOrgs[org.organizationid]" class="card mb-1">
                            <div class="card-body py-2 px-3">
                                <div class="row">
                                    <div class="col-md-4">
                                        <p class="small mb-1"><b>Email:</b> {{ org.email || '—' }}</p>
                                        <p class="small mb-1"><b>Phone:</b> {{ org.phone || '—' }}</p>
                                        <p class="small mb-1"><b>Signup:</b> {{ org.createddtm || '—' }}</p>
                                    </div>
                                    <div class="col-md-4">
                                        <p class="small mb-1"><b>Locations:</b> {{ org.location_count }}</p>
                                        <p class="small mb-1"><b>Auto-Engine:</b>
                                            {{ org.autoenginestartdt ? formatDate(org.autoenginestartdt) : '—' }} →
                                            {{ org.autoengineenddt ? formatDate(org.autoengineenddt) : '—' }}
                                        </p>
                                        <p class="small mb-1"><b>Type:</b> {{ org.type || '—' }}</p>
                                    </div>
                                    <div class="col-md-4">
                                        <p class="small mb-1 fw-bold">Admins:</p>
                                        <div v-if="org.admins && org.admins.length > 0">
                                            <span v-for="admin in org.admins" :key="admin.personid"
                                                class="badge bg-light text-dark me-1 mb-1" style="font-size:11px;">
                                                {{ admin.firstname }} {{ admin.lastname }}
                                                <span class="text-muted">({{ admin.email }})</span>
                                            </span>
                                        </div>
                                        <span v-else class="text-muted small">No admins</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Locations Table -->
                        <div class="card">
                            <div class="table-responsive" style="max-height: 500px; overflow-y: auto;">
                                <table class="table table-hover table-sm mb-0">
                                    <thead class="table-light">
                                        <tr>
                                            <th class="text-xs">Actions</th>
                                            <th class="text-xs">ID</th>
                                            <th class="text-xs">Name</th>
                                            <th class="text-xs">Status</th>
                                            <th class="text-xs">Balance</th>
                                            <th class="text-xs">#Persons</th>
                                            <th class="text-xs">#Vols</th>
                                            <th class="text-xs">#Pending</th>
                                            <th class="text-xs">Last Logon</th>
                                            <th class="text-xs">Last Schedule</th>
                                            <th class="text-xs">Last Alert</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="loc in org.locations" :key="loc.locationid">
                                            <td>
                                                <div class="d-flex gap-1">
                                                    <button class="btn btn-xs btn-success m-0 py-0 px-1"
                                                        @click="impersonate(loc)"
                                                        title="Impersonate this location (act as admin)">
                                                        <i class="material-icons" style="font-size:16px;">login</i>
                                                    </button>
                                                    <button class="btn btn-xs btn-outline-secondary m-0 py-0 px-1"
                                                        @click="copyToClipboard(loc.locationid, 'Location ID')"
                                                        title="Copy Location ID">
                                                        <i class="material-icons" style="font-size:16px;">content_copy</i>
                                                    </button>
                                                    <button v-if="canResetReminderDates"
                                                        class="btn btn-xs btn-outline-secondary m-0 py-0 px-1"
                                                        :disabled="resettingLocationId === loc.locationid"
                                                        @click="resetLocationReminderDates(loc)"
                                                        title="Reset volunteer reminder dates for this Location"
                                                        :aria-label="`Reset volunteer reminder dates for ${loc.locationname}`">
                                                        <span v-if="resettingLocationId === loc.locationid" class="spinner-border spinner-border-sm"></span>
                                                        <i v-else class="material-icons" style="font-size:16px;">restart_alt</i>
                                                    </button>
                                                </div>
                                            </td>
                                            <td class="text-xs">{{ loc.locationid }}</td>
                                            <td class="text-xs">{{ loc.locationname }}</td>
                                            <td class="text-xs">
                                                <span class="badge" :class="loc.status === 'active' ? 'bg-success' : 'bg-secondary'" style="font-size:10px;">
                                                    {{ loc.status }}
                                                </span>
                                            </td>
                                            <td class="text-xs">
                                                <span v-if="loc.balance !== null && loc.balance !== undefined"
                                                    :class="loc.balance < 0 ? 'text-danger fw-bold' : ''">
                                                    ${{ formatBalance(loc.balance) }}
                                                </span>
                                                <span v-else class="text-muted">—</span>
                                            </td>
                                            <td class="text-xs">{{ loc.persons_count }}</td>
                                            <td class="text-xs">{{ loc.volunteers_count }}</td>
                                            <td class="text-xs">{{ loc.pending_count }}</td>
                                            <td class="text-xs">{{ formatDays(loc.lastlogondayspassed) }}</td>
                                            <td class="text-xs">{{ formatDays(loc.last_activity_instance_days_passed) }}</td>
                                            <td class="text-xs">{{ formatDays(loc.lastalertdatedayspassed) }}</td>
                                        </tr>
                                        <tr v-if="!org.locations || org.locations.length === 0">
                                            <td colspan="11" class="text-center text-muted small py-2">No locations</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Pagination Footer -->
        <div class="px-4 py-2">
            <PaginationFooter :pagination="pagination" @changePage="loadPage" @updatePageSize="updatePageSize" />
        </div>

        <!-- Impersonation Confirm Modal -->
        <ContentModal v-model="showImpersonateModal" title="Impersonate Location">
            <template #default>
                <div v-if="impersonateTarget">
                    <p>You are about to assume admin role for:</p>
                    <p class="fw-bold mb-1">{{ impersonateTarget.locationname }}</p>
                    <p class="text-muted small mb-3">Location ID: {{ impersonateTarget.locationid }}</p>
                    <div class="alert alert-warning py-2 small">
                        <i class="material-icons align-middle me-1" style="font-size:16px;">warning</i>
                        This will switch your active session to this location. You will be redirected to the admin dashboard.
                        No database changes will be made.
                    </div>
                </div>
            </template>
            <template #footer>
                <button class="btn btn-sm btn-outline-secondary me-2" @click="showImpersonateModal = false">Cancel</button>
                <button class="btn btn-sm btn-success" :disabled="impersonating" @click="confirmImpersonate">
                    <span v-if="impersonating" class="spinner-border spinner-border-sm me-1"></span>
                    {{ impersonating ? 'Switching...' : 'Confirm & Switch' }}
                </button>
            </template>
        </ContentModal>

        <!-- Toast notification -->
        <div v-if="toastMessage" class="position-fixed bottom-0 end-0 p-3" style="z-index:9999;">
            <div class="toast show align-items-center text-white border-0"
                :class="toastType === 'success' ? 'bg-success' : 'bg-danger'" role="alert">
                <div class="d-flex">
                    <div class="toast-body">{{ toastMessage }}</div>
                    <button type="button" class="btn-close btn-close-white me-2 m-auto" @click="toastMessage = ''"></button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, computed, reactive } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import CardLoader from '../../components/Loader/CardLoader.vue'
import PaginationFooter from '../../components/PageLayout/PaginationFooter.vue'
import ContentModal from '../../components/Modal/ContentModal.vue'
import { setLocalStorage } from '@/utils/LocalStorageHandler'
import { usePermissions } from '@/composables/usePermissions'
import api from '@/config/api'

const store = useStore()
const router = useRouter()
const { isSuperAdmin, isOrganizationAdmin } = usePermissions()
const canResetReminderDates = computed(() => isSuperAdmin.value || isOrganizationAdmin.value)

// Loading state
const loading = ref(false)

// Pagination
const currentPage = ref(1)
const perPage = ref(10)

// Filters
const filters = reactive({
    status: 'active',
    signup_date_from: '',
    signup_date_to: '',
    balance_min: '',
    balance_max: '',
    organization_id: '',
    location_id: '',
    location_count: 1,
    location_status: 'active',
    persons_count: 1,
    search: '',
})

// Expand state
const expandedOrgs = reactive({})

// Impersonation state
const showImpersonateModal = ref(false)
const impersonateTarget = ref(null)
const impersonating = ref(false)
const resettingLocationId = ref(null)

// Toast
const toastMessage = ref('')
const toastType = ref('success')

// Computed from Vuex store
const organizations = computed(() => store.getters['allOrganizations'])
const pagination = computed(() => store.getters['organizationsPagination'])

// Fetch
const fetchOrganizations = async () => {
    loading.value = true
    try {
        await store.dispatch('fetchOrganizations', {
            search: filters.search,
            pageNumber: currentPage.value,
            pageSize: perPage.value,
            status: filters.status,
            signup_date_from: filters.signup_date_from || null,
            signup_date_to: filters.signup_date_to || null,
            balance_min: filters.balance_min !== '' ? filters.balance_min : null,
            balance_max: filters.balance_max !== '' ? filters.balance_max : null,
            organization_id: filters.organization_id || null,
            location_id: filters.location_id || null,
            location_count: filters.location_count !== '' && filters.location_count !== null ? filters.location_count : null,
            location_status: filters.location_status || null,
            persons_count: filters.persons_count !== '' && filters.persons_count !== null ? filters.persons_count : null,
        })
    } catch (err) {
        console.error('Error loading organizations:', err)
    } finally {
        loading.value = false
    }
}

const applyFilters = () => {
    currentPage.value = 1
    fetchOrganizations()
}

const clearFilters = () => {
    filters.status = 'active'
    filters.signup_date_from = ''
    filters.signup_date_to = ''
    filters.balance_min = ''
    filters.balance_max = ''
    filters.organization_id = ''
    filters.location_id = ''
    filters.location_count = 1
    filters.location_status = 'active'
    filters.persons_count = 1
    filters.search = ''
    applyFilters()
}

const loadPage = (page) => {
    if (!page) return
    currentPage.value = page
    fetchOrganizations()
}

const updatePageSize = (size) => {
    perPage.value = size
    currentPage.value = 1
    fetchOrganizations()
}

const resetLocationReminderDates = async (location) => {
    if (!window.confirm(`Reset volunteer reminder dates for all members of ${location.locationname}? This does not send emails immediately.`)) return

    resettingLocationId.value = location.locationid
    try {
        const response = await api.post('/reminder-dates/location/reset', {
            location_id: location.locationid,
        })
        await fetchOrganizations()
        showToast(response.data?.message || 'Volunteer reminder dates reset.', 'success')
    } catch (err) {
        showToast(err.response?.data?.message || err.message || 'Failed to reset reminder dates.', 'error')
    } finally {
        resettingLocationId.value = null
    }
}

// Expand/collapse
const toggleExpand = (orgId) => {
    expandedOrgs[orgId] = !expandedOrgs[orgId]
}

// Impersonation
const impersonate = (location) => {
    impersonateTarget.value = location
    showImpersonateModal.value = true
}

const confirmImpersonate = async () => {
    if (!impersonateTarget.value) return
    impersonating.value = true
    try {
        const sessionData = await store.dispatch('impersonateLocation', impersonateTarget.value.locationid)
        // Update session in store and localStorage (covers dashboard + billing)
        store.commit('SET_SESSION_DATA', sessionData)
        setLocalStorage('sessionData', sessionData)

        // Set cookies for microfrontends that read cslocationid / cslocationname
        // (setupwizard, manageschedules, personschedule, addusers)
        const maxAge = 525600 * 60 // 1 year in seconds
        document.cookie = `cslocationid=${sessionData.locationid};path=/;max-age=${maxAge};SameSite=Lax`
        document.cookie = `cslocationname=${encodeURIComponent(impersonateTarget.value.locationname || '')};path=/;max-age=${maxAge};SameSite=Lax`

        // Set setupwizard-compatible localStorage keys
        setLocalStorage('sessiondetails', sessionData)
        setLocalStorage('locid', String(sessionData.locationid))

        // Clear api cache so dashboard fetches fresh data
        Object.keys(localStorage).forEach(key => {
            if (key.startsWith('apiCache_')) localStorage.removeItem(key)
        })
        showImpersonateModal.value = false
        showToast(`Switched to ${impersonateTarget.value.locationname}. Redirecting...`, 'success')
        setTimeout(() => {
            router.push('/admin')
            // Force reload to reinitialize all components with new session
            setTimeout(() => window.location.reload(), 200)
        }, 800)
    } catch (err) {
        showToast('Failed to impersonate: ' + (err.message || 'Unknown error'), 'error')
    } finally {
        impersonating.value = false
    }
}

// Clipboard
const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(String(text)).then(() => {
        showToast(`${label} "${text}" copied!`, 'success')
    }).catch(() => {
        showToast('Copy failed', 'error')
    })
}

// Formatting helpers
const formatBalance = (val) => {
    if (val === null || val === undefined) return '—'
    return Number(val).toFixed(2)
}

const formatDays = (days) => {
    if (days === null || days === undefined || days >= 1000000) return '—'
    if (days === 0) return 'Today'
    return `${days}d`
}

const formatDate = (dtm) => {
    if (!dtm) return '—'
    try {
        return new Date(dtm).toLocaleDateString()
    } catch {
        return dtm
    }
}

// Toast helper
let toastTimer = null
const showToast = (msg, type = 'success') => {
    toastMessage.value = msg
    toastType.value = type
    if (toastTimer) clearTimeout(toastTimer)
    toastTimer = setTimeout(() => { toastMessage.value = '' }, 3000)
}

// Lifecycle
onMounted(() => {
    fetchOrganizations()
})
</script>

<style scoped>
.cursor-pointer {
    cursor: pointer;
}
.text-xs {
    font-size: 12px !important;
}
.btn-xs {
    padding: 0 4px;
    font-size: 12px;
    line-height: 1.4;
}
.table th {
    white-space: nowrap;
    padding: 6px 8px !important;
}
.table td {
    padding: 4px 8px !important;
    vertical-align: middle;
}
.toast {
    min-width: 250px;
}
</style>
